"use client";

import { usePathname } from "next/navigation";

import { ReactNode, useEffect, useState } from "react";

import { createInstance } from "i18next";
import { I18nextProvider } from "react-i18next";

import type { i18n as I18nInstance, Resource } from "i18next";

import initI18nInstance from "@/lib/i18nConfigs/initI18nInstance";
import { attachResourcesBackend } from "@/lib/i18nConfigs/resourcesBackend";
import {
  ALL_I18N_NAMESPACES,
  resolveI18nNamespaces,
} from "@/lib/i18nConfigs/routeNamespaces";

export default function TranslationsProvider({
  children,
  locale,
  namespaces,
  resources,
}: {
  children: ReactNode;
  locale: string;
  namespaces: string[];
  resources: Resource;
}) {
  const [i18n] = useState<I18nInstance>(() => {
    const instance = createInstance();
    initI18nInstance({
      locale,
      namespaces,
      i18nInstance: instance,
      resources,
      useBackend: attachResourcesBackend,
    });
    return instance;
  });

  const pathname = usePathname();

  useEffect(() => {
    if (!pathname) {
      return;
    }

    i18n.loadNamespaces(resolveI18nNamespaces(pathname));
  }, [i18n, pathname]);

  useEffect(() => {
    const warm = () => {
      i18n.loadNamespaces(ALL_I18N_NAMESPACES);
    };

    if (typeof window.requestIdleCallback === "function") {
      const handle = window.requestIdleCallback(warm);

      return () => {
        window.cancelIdleCallback?.(handle);
      };
    }

    const handle = window.setTimeout(warm, 1200);

    return () => {
      window.clearTimeout(handle);
    };
  }, [i18n]);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
